import React, { useEffect, useState } from 'react';
import { useForm, SubmitHandler } from 'react-hook-form';
import { useNavigate } from 'react-router-dom';

import character from '../../assets/images/intake/step3/life_stage.svg';
import { useIntakeFormContext } from '../../hooks/use-form-context.hooks';
import { dispatchUpdateIntakeFormEducation } from '../../redux/slice/form-intake/form-intake.operations';
import {
  EducationLevel,
  IntakeFormEducationDTO,
  WorkStatus as WorkStatusValue,
} from '../../redux/slice/form-intake/form-intake.types';
import { useAppDispatch } from '../../redux/store';
interface FormData {
  education: string;
  workStatus: string[];
  workStatusComments?: string;
}
interface WorkStatusProps {
  step: number;
  onBack: () => void;
}
const education_options = [
  {
    value: EducationLevel.FRESHMAN,
    label: 'Freshman',
    sub_label: '9th grade',
  },
  {
    value: EducationLevel.SOPHOMORE,
    label: 'Sophomore',
    sub_label: '10th grade',
  },
  {
    value: EducationLevel.JUNIOR,
    label: 'Junior',
    sub_label: '11th grade',
  },
  {
    value: EducationLevel.SENIOR,
    label: 'Senior',
    sub_label: '12th grade',
  },
  {
    value: EducationLevel.SOME_COLLEGE,
    label: 'Some College',
  },
  {
    value: EducationLevel.COLLEGE_GRADUATE,
    label: 'College Graduate',
  },
];

const work_options = [
  {
    value: WorkStatusValue.COLLEGE_TEST_PREP,
    label: 'College Test Prep',
  },
  {
    value: WorkStatusValue.CAREER_COACHING,
    label: 'Career Coaching',
  },
  {
    value: WorkStatusValue.NO_COLLEGE_TEST_PREP,
    label: 'No College Test Prep',
  },
  {
    value: WorkStatusValue.NO_CAREER_COACHING,
    label: 'No Career Coaching',
  },
  {
    value: WorkStatusValue.INTERNSHIP,
    label: 'Internship',
  },
  {
    value: WorkStatusValue.APPRENTICESHIP,
    label: 'Apprenticeship',
  },
  {
    value: WorkStatusValue.BOOTCAMP_CODING,
    label: 'Bootcamp/coding',
  },
  {
    value: WorkStatusValue.ENTREPRENEURSHIP,
    label: 'Entrepreneurship',
  },
  {
    value: WorkStatusValue.COMMUNITY_COLLEGE,
    label: 'Community College',
    sub_label: '2 Year College',
  },
  {
    value: WorkStatusValue.VOCATIONAL,
    label: 'Vocational',
    sub_label: 'Trade School',
  },
  {
    value: WorkStatusValue.PHD,
    label: 'PHD or Law Degree',
  },
  {
    value: WorkStatusValue.POST_GRADUATE,
    label: 'Post Graduate',
    sub_label: 'Graduate School',
  },
  {
    value: WorkStatusValue.UNEMPLOYED,
    label: 'Unemployed',
  },
  {
    value: WorkStatusValue.MILITARY_SERVICE,
    label: 'Military service/schooling/training',
  },
  {
    value: WorkStatusValue.WORK_PART_TIME,
    label: 'Work part time',
    sub_label: 'Casual',
  },
  {
    value: WorkStatusValue.WORK_FULL_TIME,
    label: 'Working full time',
  },
];

export const WorkStatus: React.FC<WorkStatusProps> = ({ step, onBack }) => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const { education, workStatus, workStatusComments } = useIntakeFormContext();
  const [selectedEducation, setSelectedEducation] = useState(education || '');
  const [otherRemarks, setOtherRemarks] = useState(workStatusComments || '');
  const [selectedWorkStatus, setSelectedWorkStatus] = useState<string[]>(workStatus || []);
  const [error, setError] = useState({
    education: '',
    workStatus: '',
  });
  const textAreaChange = (event: React.ChangeEvent<HTMLTextAreaElement>) => {
    setOtherRemarks(event.target.value);
  };
  const handleEducationChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setSelectedEducation(event.target.value);
    setError({ ...error, education: '' });
  };
  const handleWorkStatusChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const value = event.target.value;
    if (selectedWorkStatus.includes(value)) {
      setSelectedWorkStatus(selectedWorkStatus.filter((val) => val !== value));
    } else {
      setSelectedWorkStatus([...selectedWorkStatus, value]);
      setError({ ...error, workStatus: '' });
    }
  };
  useEffect(() => {
    window.scrollTo(0, 0); // Reset scroll to the top
  }, []);
  useEffect(() => {
    setSelectedEducation(education ?? '');
    setOtherRemarks(workStatusComments ?? '');
    setSelectedWorkStatus(workStatus ?? []);
  }, [education, workStatus, workStatusComments]);
  const { handleSubmit } = useForm<FormData>();
  const submitWorkStatus: SubmitHandler<FormData> = async () => {
    const isOtherSelected: boolean = selectedWorkStatus.includes(WorkStatusValue.OTHER);
    if (!selectedEducation) {
      setError({ ...error, education: 'Please select an option' });
    } else if (!selectedWorkStatus.length) {
      setError({ ...error, workStatus: 'Please select at least one option.' });
    } else if (!isOtherSelected && otherRemarks?.trim()) {
      setError({ ...error, workStatus: 'Please select the Other(Explain) option.' });
    } else {
      const dto: IntakeFormEducationDTO = {
        education: selectedEducation as EducationLevel,
        workStatus: selectedWorkStatus as WorkStatusValue[],
        workStatusComments: isOtherSelected ? otherRemarks?.trim() : undefined,
      };
      await dispatch(dispatchUpdateIntakeFormEducation(dto));

      navigate(`/pre-employment/intake/step${step + 1}`);
    }
  };
  const backButtonClick = () => {
    onBack();
  };
  return (
    <div>
      <div className="flex mt-9 mb-10 md:px-10">
        <div className="bg-gray-200 p-4 rounded-lg font-semibold mr-5 flex items-center">03</div>
        <div>
          <h4 className="text-2xl">
            <span className="font-semibold"> Education</span> &/Or
            <span className="font-semibold"> Work Status</span>
          </h4>
          <p className="text-lg">where are you in your education journey is more important</p>
        </div>
      </div>
      <div className="character absolute top-96 left-[-210px] z-[-1]">
        <img className="" src={character} alt="" />
      </div>
      <form
        className="md:px-10"
        action=""
        // eslint-disable-next-line @typescript-eslint/no-misused-promises
        onSubmit={handleSubmit(submitWorkStatus)}
      >
        <div className="grid grid-cols-2 gap-x-6 gap-y-3">
          {education_options.map((option) => (
            <div key={option.label} className="h-[60px]">
              <label className="radio-container h-full p-4 block relative">
                <input
                  checked={selectedEducation === option.value}
                  onChange={handleEducationChange}
                  className="w-full h-full absolute top-0 left-0 opacity-0"
                  type="radio"
                  value={option.value}
                  name="work status"
                />
                <span className="checkmark font-semibold">
                  <span>
                    {option.label}
                    {option.sub_label && (
                      <span className="block font-light text-sm">{option.sub_label}</span>
                    )}
                  </span>
                </span>
              </label>
            </div>
          ))}
          {error.education && <p className="text-red-500">{error.education}</p>}
        </div>
        <p className="text-lg border-t border-purple-700 mt-10 pt-10 mb-7">
          <span className="font-bold">Where are you now?</span> Pick all that apply to you
        </p>
        <div className="grid grid-cols-2 gap-x-6 gap-y-3">
          {work_options.map((option) => (
            <div key={option.label} className="h-[60px]">
              <label className="checkbox-container h-full p-4 block relative">
                <input
                  checked={selectedWorkStatus.includes(option.value)}
                  onChange={handleWorkStatusChange}
                  className="w-full h-full absolute top-0 left-0 opacity-0"
                  type="checkbox"
                  value={option.value}
                />
                <span className="checkbox-checkmark font-semibold">
                  <span>
                    {option.label}
                    {option.sub_label && (
                      <span className="block font-light text-sm">{option.sub_label}</span>
                    )}
                  </span>
                </span>
              </label>
            </div>
          ))}
        </div>
        <div className="grid mt-3">
          <div className="h-[170px]">
            <label className="checkbox-container h-full p-4 block relative">
              <input
                checked={selectedWorkStatus.includes(WorkStatusValue.OTHER)}
                onChange={handleWorkStatusChange}
                value="other"
                className="w-full h-full absolute top-0 left-0 opacity-0"
                type="checkbox"
              />
              <span className="checkbox-checkmark font-semibold !block h-auto pt-4">
                Other(Explain)
                <textarea
                  onChange={textAreaChange}
                  value={otherRemarks}
                  rows={2}
                  className="bg-white border rounded-lg w-full resize-none mt-4 font-light p-3"
                  name=""
                ></textarea>
              </span>
            </label>
          </div>
        </div>
        {error.workStatus && <p className="text-red-500">{error.workStatus}</p>}
        <div className="flex justify-between mt-8">
          <button
            // eslint-disable-next-line @typescript-eslint/no-misused-promises
            onClick={handleSubmit(backButtonClick)}
            className="btn-primary"
          >
            Back
          </button>
          <button className="btn-primary">Next</button>
        </div>
      </form>
    </div>
  );
};
